import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import IosShareIcon from "@mui/icons-material/IosShare";
import { toast } from "sonner";
import { delftBlue, flame } from "@/theme/colors";
import {
  useGetFormSubmissionByIdQuery,
  useDownloadFormSubmissionMutation,
} from "@/app/features/dashboard/dashboardApiSlice";
import {
  fallbackDownloadFilename,
  getDeviceTimeZone,
  getDownloadErrorMessage,
  isNativeShareAvailable,
  openEmailDraftWithAttachment,
  parseContentDispositionFilename,
  saveBlobFile,
  shareFormFile,
} from "@/utils/form-download";
import { getFormEmailContent } from "@/constants/form-email";

const FormDetailsPage = () => {
  const { formType, id } = useParams();
  const navigate = useNavigate();
  const [fileAction, setFileAction] = useState(null);
  const fileCacheRef = useRef({ key: "", promise: null });
  const canNativeShare = isNativeShareAvailable();

  const {
    data: submissionResponse,
    isLoading,
    error,
  } = useGetFormSubmissionByIdQuery({ formType, id });
  const [downloadFormSubmission] = useDownloadFormSubmissionMutation();

  const submission = submissionResponse?.data?.submission;
  const renderedSections = submission?.renderedSections || [];

  const fetchDownloadedFile = async () => {
    const timeZone = getDeviceTimeZone();
    const payload = await downloadFormSubmission({
      formType,
      id,
      ...(timeZone ? { timeZone } : {}),
    }).unwrap();
    if (!payload?.blob) {
      throw new Error(payload?.message || "Failed to download form submission");
    }
    const filename =
      parseContentDispositionFilename(payload.filename) ||
      fallbackDownloadFilename(formType, payload.contentType || payload.blob.type);
    const contentType =
      payload.contentType || payload.blob.type || "application/octet-stream";
    const file = new File([payload.blob], filename, { type: contentType });
    return { blob: payload.blob, file, filename };
  };

  const ensureDownloadedFile = () => {
    const key = `${formType}:${id}:${getDeviceTimeZone()}`;
    if (fileCacheRef.current.key !== key || !fileCacheRef.current.promise) {
      fileCacheRef.current = {
        key,
        promise: fetchDownloadedFile().catch((downloadError) => {
          fileCacheRef.current = { key: "", promise: null };
          throw downloadError;
        }),
      };
    }
    return fileCacheRef.current.promise;
  };

  const handleDownload = async () => {
    setFileAction("download");
    try {
      const { blob, filename } = await ensureDownloadedFile();
      saveBlobFile(blob, filename);
      toast.success("Form downloaded");
    } catch (downloadError) {
      toast.error(await getDownloadErrorMessage(downloadError));
    } finally {
      setFileAction(null);
    }
  };

  const handleShare = async () => {
    setFileAction("share");
    try {
      const { file, filename } = await ensureDownloadedFile();
      const { subject, body } = getFormEmailContent({
        formName: submission?.formName,
        guideName:
          submission?.submittedBy?.fullName ||
          submission?.submittedBy?.username ||
          submission?.submittedBy?.email,
        companyName: submission?.companyId?.name || submission?.company?.name,
        activityName: submission?.activityId?.name || submission?.activity?.name,
        submittedAt: formatDate(submission?.submittedAt || submission?.createdAt),
        filename,
      });
      const shareResult = await shareFormFile(file, {
        title: subject,
        text: body,
      });
      if (shareResult === "shared" || shareResult === "cancelled") {
        return;
      }
      await openEmailDraftWithAttachment(file, { subject, body });
      toast.success(
        "This browser has no system Share sheet. An email file was downloaded with the form attached — open it to send.",
      );
    } catch (downloadError) {
      toast.error(await getDownloadErrorMessage(downloadError));
    } finally {
      setFileAction(null);
    }
  };

  const isFileActionLoading = Boolean(fileAction);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [formType, id, isLoading]);

  const S3_BASE_URL = "https://s3.ap-southeast-2.amazonaws.com/op-docx-bucket-prod";
  const CLOUDFRONT_URL = "https://d3hjliepkppdbb.cloudfront.net/";

  const coerceFileUrl = (fileUrl) => {
    if (!fileUrl) return "";
    if (typeof fileUrl === "string") return fileUrl;
    if (Array.isArray(fileUrl)) return coerceFileUrl(fileUrl[0]);
    if (typeof fileUrl === "object") {
      return coerceFileUrl(
        fileUrl.url ||
          fileUrl.fileUrl ||
          fileUrl.signedUrl ||
          fileUrl.location ||
          fileUrl.key ||
          fileUrl.path ||
          fileUrl.signature,
      );
    }
    return "";
  };

  const formatFileUrl = (fileUrl) => {
    const rawUrl = coerceFileUrl(fileUrl);
    if (!rawUrl) return "";
    if (rawUrl.includes(S3_BASE_URL)) {
      return rawUrl.replace(S3_BASE_URL, CLOUDFRONT_URL);
    }
    return rawUrl.startsWith("http") ? rawUrl : `${CLOUDFRONT_URL}${rawUrl}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isEmptyAnswer = (value) => {
    if (value == null || value === "" || value === "—") return true;
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
  };

  const normalizeLookupKey = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const snakeToCamel = (value = "") =>
    String(value).replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

  const labelToCamel = (label = "") => {
    const words = String(label)
      .replace(/\(.*?\)/g, " ")
      .replace(/[^a-zA-Z0-9]+/g, " ")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!words.length) return "";
    return words
      .map((word, index) => {
        const lower = word.toLowerCase();
        if (index === 0) return lower;
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join("");
  };

  const isAnswerChecked = (answer) => {
    if (answer == null || answer === "") return false;
    if (typeof answer === "boolean") return answer;
    if (typeof answer === "number") return answer !== 0;
    if (typeof answer === "string") {
      const normalized = answer.trim().toLowerCase();
      return normalized === "true" || normalized === "yes" || normalized === "checked";
    }
    if (Array.isArray(answer)) {
      return answer.some((item) => item === true || item === "true" || Boolean(item?.checked));
    }
    if (typeof answer === "object") {
      return Boolean(answer.checked || answer.selected || answer.value === true);
    }
    return false;
  };

  const getOptionNote = (answer, option) =>
    option?.note ||
    (answer && typeof answer === "object" && !Array.isArray(answer) ? answer.note || answer.notes : "") ||
    "";

  const OPTION_KEY_ALIASES = {
    pfds: ["pdfs"],
    pdfs: ["pfds"],
  };

  const optionAliasKeys = (value = "") => OPTION_KEY_ALIASES[normalizeLookupKey(value)] || [];

  const getSubmissionAnswerForOption = (option) => {
    if (!option || !submission) return undefined;
    const directKeys = [
      option.value,
      option.id,
      snakeToCamel(option.value || ""),
      labelToCamel(option.label || ""),
      ...optionAliasKeys(option.value || ""),
      ...optionAliasKeys(labelToCamel(option.label || "")),
    ].filter(Boolean);

    for (const key of directKeys) {
      if (submission[key] != null && submission[key] !== "") return submission[key];
      if (submission[`${key}s`] != null && submission[`${key}s`] !== "") return submission[`${key}s`];
    }

    const needles = new Set(
      [option.value, option.label, ...optionAliasKeys(option.value || "")]
        .filter(Boolean)
        .map((item) => normalizeLookupKey(item)),
    );
    if (!needles.size) return undefined;
    const matches = Object.keys(submission).filter((key) => {
      const normalizedKey = normalizeLookupKey(key);
      return [...needles].some(
        (needle) => normalizedKey === needle || (needle.length >= 4 && normalizedKey.startsWith(needle)),
      );
    });
    if (matches.length === 1) return submission[matches[0]];
    const checkedMatch = matches.find((key) => isAnswerChecked(submission[key]));
    return checkedMatch ? submission[checkedMatch] : undefined;
  };

  const findFormConfigField = (field) => {
    const pages = submission?.formConfig?.pages || [];
    for (const page of pages) {
      for (const section of page.sections || []) {
        for (const configField of section.fields || []) {
          if (
            (field.id && (configField.id === field.id || configField.fieldId === field.id)) ||
            (field.fieldId && (configField.id === field.fieldId || configField.fieldId === field.fieldId)) ||
            (field.label && (configField.id === field.label || configField.label === field.label))
          ) {
            return configField;
          }
        }
      }
    }
    return null;
  };

  const getCheckboxGroupLabel = (field) => {
    const configField = findFormConfigField(field);
    const raw = configField?.label || configField?.question || field?.label || field?.question || "";
    if (!raw) return "";
    const machineIds = new Set(
      [field?.id, field?.fieldId, configField?.id, configField?.fieldId].filter(Boolean).map(String),
    );
    if (machineIds.has(raw)) return "";
    return raw;
  };

  const resolveCheckboxGroupOptions = (field) => {
    const configField = findFormConfigField(field);
    const merged = new Map();
    [...(configField?.options || []), ...(field.options || [])].forEach((option) => {
      const key = option?.value || option?.label;
      if (!key) return;
      merged.set(key, { ...(merged.get(key) || {}), ...option });
    });

    return Array.from(merged.values()).map((option) => {
      const answer = getSubmissionAnswerForOption(option);
      const checked = Boolean(option.checked) || isAnswerChecked(answer);
      return {
        ...option,
        checked,
        note: getOptionNote(answer, option),
      };
    });
  };

  const isSignatureRef = (url) => /signature/i.test(String(url || ""));

  const fileListFromValue = (value) => {
    if (Array.isArray(value)) return value.filter((item) => typeof item === "string" && item);
    if (typeof value !== "string") return [];
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.filter((item) => typeof item === "string" && item);
      } catch {
        return [];
      }
    }
    return [trimmed];
  };

  const isSignatureOnlyFileList = (value) => {
    const files = fileListFromValue(value);
    return files.length > 0 && files.every((item) => isSignatureRef(item));
  };

  const collectSignatureRefs = () => {
    const refs = new Set();
    const add = (value) => {
      if (!value) return;
      if (typeof value === "string") {
        refs.add(value);
        return;
      }
      if (Array.isArray(value)) {
        value.forEach(add);
        return;
      }
      if (typeof value === "object") {
        add(value.signature || value.url || value.fileUrl || value.key || value.path);
      }
    };
    Object.entries(submission || {}).forEach(([key, value]) => {
      if (/signature/i.test(key)) add(value);
    });
    (submission?.staffMembers || []).forEach((member) => add(member?.signature));
    (submission?.renderedSections || []).forEach((section) => {
      (section.fields || []).forEach((fieldItem) => {
        if (fieldItem.kind === "signature") add(fieldItem.value);
        (fieldItem.staffMembers || []).forEach((member) => add(member?.signature));
      });
    });
    return refs;
  };

  const filesForField = (field) => {
    const signatureRefs = collectSignatureRefs();
    return (field?.files || [])
      .map(coerceFileUrl)
      .filter((url) => url && !isSignatureRef(url) && !signatureRefs.has(url));
  };

  const hasVisibleAnswer = (field) => {
    if (!field) return false;
    if (field.type === "confirmation-checkbox") return true;
    if (field.kind === "radio") return field.value != null || Boolean(field.note);
    if (field.kind === "checkbox") return Boolean(field.value) || Boolean(field.note);
    if (field.kind === "checkbox-group") {
      return resolveCheckboxGroupOptions(field).some((option) => option.checked || option.note);
    }
    if (field.kind === "files") return filesForField(field).length > 0;
    if (field.kind === "signature") return Boolean(field.value);
    if (field.kind === "staff") {
      return (field.staffMembers || []).some((member) => member?.name || member?.signature);
    }
    if (isSignatureOnlyFileList(field.value)) return false;
    return !isEmptyAnswer(field.value);
  };

  const renderField = (label, value) => {
    if (isEmptyAnswer(value) || isSignatureOnlyFileList(value)) return null;
    const display = typeof value === "object" ? JSON.stringify(value, null, 2) : String(value);
    return (
      <Box sx={{ marginBottom: 2 }}>
        <Typography
          sx={{
            color: delftBlue[500],
            fontSize: { xs: 12, sm: 13 },
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 600,
            marginBottom: 0.5,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            color: delftBlue[700],
            fontSize: { xs: 14, sm: 15 },
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 400,
          }}
        >
          {display}
        </Typography>
      </Box>
    );
  };

  const renderRadioField = (label, yesNo, note) => (
    <Box sx={{ marginBottom: 2 }}>
      <Typography
        sx={{
          color: delftBlue[500],
          fontSize: { xs: 12, sm: 13 },
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 600,
          marginBottom: 0.5,
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip label={yesNo ? "Yes" : "No"} color={yesNo ? "success" : "default"} size="small" />
        {note ? (
          <Typography sx={{ color: delftBlue[700], fontSize: { xs: 13, sm: 14 }, fontFamily: "Montserrat, sans-serif", fontStyle: "italic" }}>
            {note}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );

  const renderCheckboxField = (label, checked, note) => {
    if (!checked && !note) return null;
    if (!checked) return null;
    return (
      <Box sx={{ marginBottom: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={label} color="success" size="small" />
          {note ? (
            <Typography
              sx={{
                color: delftBlue[700],
                fontSize: { xs: 13, sm: 14 },
                fontFamily: "Montserrat, sans-serif",
                fontStyle: "italic",
              }}
            >
              {note}
            </Typography>
          ) : null}
        </Stack>
      </Box>
    );
  };

  const renderImage = (url, alt) => {
    if (!url) return null;
    const formattedUrl = formatFileUrl(url);
    if (!formattedUrl) return null;
    return (
      <Box sx={{ marginBottom: 2 }}>
        <img
          src={formattedUrl}
          alt={alt}
          style={{
            maxWidth: "100%",
            height: "auto",
            borderRadius: 8,
            border: `1px solid ${delftBlue[200]}`,
          }}
        />
      </Box>
    );
  };

  const renderSignatureCard = (url, alt) => {
    if (!url) return null;
    const formattedUrl = formatFileUrl(url);
    if (!formattedUrl) return null;
    return (
      <Box
        sx={{
          display: "inline-block",
          marginRight: 1,
          marginBottom: 1,
          padding: 1,
          borderRadius: 2,
          border: `1px solid ${delftBlue[200]}`,
          background: "white",
          maxWidth: { xs: "120px", sm: "150px" },
        }}
      >
        <img
          src={formattedUrl}
          alt={alt}
          style={{ width: "100%", height: "auto", borderRadius: 4, display: "block" }}
        />
      </Box>
    );
  };

  const renderUploadedDocumentsList = (urls) => {
    const documentUrls = (urls || []).filter((url) => typeof url === "string" && url);
    if (!documentUrls.length) return null;
    return (
      <Box sx={{ marginBottom: 2 }}>
        <Typography
          sx={{
            color: delftBlue[500],
            fontSize: { xs: 12, sm: 13 },
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 600,
            marginBottom: 1,
          }}
        >
          Uploaded Documents
        </Typography>
        <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
          {documentUrls.map((url, index) => (
            <Button
              key={`${url}-${index}`}
              variant="outlined"
              href={formatFileUrl(url)}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textTransform: "none", width: "auto", flexShrink: 0 }}
            >
              Document {index + 1}
            </Button>
          ))}
        </Stack>
      </Box>
    );
  };

  const renderAnswer = (field) => {
    if (!hasVisibleAnswer(field)) return null;
    if (field.kind === "radio") return renderRadioField(field.label, field.value, field.note);
    if (field.type === "confirmation-checkbox") return renderRadioField(field.label, Boolean(field.value), field.note);
    if (field.kind === "checkbox") {
      return (
        <Box sx={{ marginBottom: 2 }}>
          <Typography
            sx={{
              color: delftBlue[500],
              fontSize: { xs: 12, sm: 13 },
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 600,
              marginBottom: 1,
            }}
          >
            {field.label}
          </Typography>
          {renderCheckboxField("Yes", field.value, field.note)}
        </Box>
      );
    }
    if (field.kind === "checkbox-group") {
      const options = resolveCheckboxGroupOptions(field).filter((option) => option.checked);
      const questionLabel = getCheckboxGroupLabel(field);
      return (
        <Box sx={{ marginBottom: 2 }}>
          {questionLabel ? (
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: { xs: 12, sm: 13 },
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 600,
                marginBottom: 1,
              }}
            >
              {questionLabel}
            </Typography>
          ) : null}
          {options.map((option) => (
            <Box key={option.value || option.label}>
              {renderCheckboxField(option.label, option.checked, option.note)}
            </Box>
          ))}
        </Box>
      );
    }
    if (field.kind === "files") {
      const files = filesForField(field);
      const images = files.filter((url) => /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(url) || url.includes("image"));
      const documents = files.filter((url) => !images.includes(url));
      return (
        <Box>
          {images.length > 0 && (
            <Box sx={{ marginBottom: 2 }}>
              <Typography
                sx={{
                  color: delftBlue[500],
                  fontSize: { xs: 12, sm: 13 },
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600,
                  marginBottom: 1,
                }}
              >
                Uploaded Images
              </Typography>
              <Stack spacing={2}>
                {images.map((url, index) => (
                  <Box key={`${url}-${index}`}>{renderImage(url, `Uploaded Image ${index + 1}`)}</Box>
                ))}
              </Stack>
            </Box>
          )}
          {renderUploadedDocumentsList(documents.length ? documents : images.length ? [] : files)}
        </Box>
      );
    }
    if (field.kind === "signature") {
      return (
        <Box sx={{ marginBottom: 2 }}>
          <Typography
            sx={{
              color: delftBlue[500],
              fontSize: { xs: 12, sm: 13 },
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 600,
              marginBottom: 1,
            }}
          >
            {field.label}
          </Typography>
          {renderSignatureCard(field.value, field.label)}
        </Box>
      );
    }
    if (field.kind === "staff") {
      const staffMembers = (field.staffMembers || []).filter((member) => member?.name || member?.signature);
      return (
        <Box>
          <Typography
            sx={{
              color: delftBlue[500],
              fontSize: { xs: 12, sm: 13 },
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 600,
              marginBottom: 1,
            }}
          >
            {field.label || "Staff"}
          </Typography>
          {staffMembers.map((member, index) => (
            <Box key={`${member?.name || "staff"}-${index}`} sx={{ marginBottom: 2 }}>
              {member?.name ? (
                <Typography
                  sx={{
                    color: delftBlue[700],
                    fontSize: { xs: 14, sm: 15 },
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                  }}
                >
                  {member.name}
                </Typography>
              ) : null}
              {member?.signature ? renderSignatureCard(member.signature, `${member.name || "Staff"} Signature`) : null}
            </Box>
          ))}
        </Box>
      );
    }
    return renderField(field.label, field.value);
  };

  const sectionPaperSx = {
    padding: { xs: 2, sm: 3 },
    borderRadius: 2,
    outline: `1px ${delftBlue[100]} solid`,
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="error">{error?.data?.message || "Failed to load form submission"}</Alert>
        <Button onClick={() => navigate("/all-forms")} sx={{ marginTop: 2 }}>
          Back to Submitted Forms
        </Button>
      </Box>
    );
  }

  if (!submission) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="warning">Form submission not found</Alert>
        <Button onClick={() => navigate("/all-forms")} sx={{ marginTop: 2 }}>
          Back to Submitted Forms
        </Button>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        alignSelf: "stretch",
        minHeight: "100vh",
        paddingTop: { xs: 2, sm: 2.5, md: 3 },
        paddingLeft: { xs: 1.5, sm: 2, md: 3 },
        paddingRight: { xs: 1.5, sm: 2, md: 3 },
        paddingBottom: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <Stack spacing={3}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate("/all-forms")}
            sx={{
              borderRadius: 2,
              borderColor: delftBlue[200],
              color: delftBlue[500],
              textTransform: "none",
              "&:hover": { borderColor: delftBlue[300], background: delftBlue[50] },
            }}
          >
            Back to Submitted Forms
          </Button>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={fileAction === "download" ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
              onClick={handleDownload}
              disabled={isFileActionLoading}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                backgroundColor: flame[500],
                "&:hover": { backgroundColor: flame[600] },
              }}
            >
              Download
            </Button>
            <Button
              variant="outlined"
              startIcon={fileAction === "share" ? <CircularProgress size={16} color="inherit" /> : <IosShareIcon />}
              onClick={handleShare}
              onMouseEnter={ensureDownloadedFile}
              onFocus={ensureDownloadedFile}
              disabled={isFileActionLoading}
              sx={{
                borderRadius: 2,
                borderColor: delftBlue[200],
                color: delftBlue[500],
                textTransform: "none",
                "&:hover": { borderColor: delftBlue[300], background: delftBlue[50] },
              }}
            >
              {canNativeShare ? "Share" : "Email"}
            </Button>
          </Stack>
        </Box>
        <Box>
          <Typography
            sx={{
              color: delftBlue[500],
              fontSize: { xs: 22, sm: 24, md: 28 },
              fontFamily: "Audiowide, sans-serif",
              fontWeight: 400,
              lineHeight: 1.29,
              marginBottom: 1,
            }}
          >
            {submission.formName || "Form Submission Details"}
          </Typography>
          <Typography
            sx={{
              color: delftBlue[500],
              fontSize: { xs: 13, sm: 14, md: 16 },
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 400,
              lineHeight: 1.5,
            }}
          >
            Submitted on {formatDate(submission.submittedAt || submission.createdAt)}
          </Typography>
        </Box>

        <Paper sx={sectionPaperSx}>
          <Typography sx={{ color: delftBlue[500], fontSize: { xs: 16, sm: 18 }, fontFamily: "Audiowide, sans-serif", fontWeight: 400, marginBottom: 2 }}>
            Submission Information
          </Typography>
          <Divider sx={{ marginBottom: 2 }} />
          {renderField("User Name", submission.submittedBy?.fullName || submission.submittedBy?.username || submission.submittedBy?.email)}
          {renderField("Company", submission.companyId?.name || submission.company?.name)}
          {renderField("Activity", submission.activityId?.name || submission.activity?.name)}
          {renderField("Submitted At", formatDate(submission.submittedAt || submission.createdAt))}
        </Paper>

        {renderedSections.map((section, index) => {
          const fields = (section.fields || []).filter(hasVisibleAnswer);
          if (!fields.length) return null;
          return (
            <Paper key={`${section.title}-${index}`} sx={sectionPaperSx}>
              <Typography sx={{ color: delftBlue[500], fontSize: { xs: 16, sm: 18 }, fontFamily: "Audiowide, sans-serif", fontWeight: 400, marginBottom: 2 }}>
                {section.title}
              </Typography>
              <Divider sx={{ marginBottom: 2 }} />
              {fields.map((field) => (
                <Box key={field.id}>{renderAnswer(field)}</Box>
              ))}
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
};

export default FormDetailsPage;
